import { Injectable } from '@angular/core';
import { ApplicationContext } from '../models/application-context.model';
import { Observable, Subject, catchError, map, of, switchMap } from 'rxjs';
import { AuthenticationService, User, UserCompany } from './auth.service';
import { StudiesCompanyAutoSelectService } from './studies-company-auto-select.service';
import {
  getVisibleUserCompanies,
  resolveStudiesOrganizationContext,
} from '../utils/studies-company.util';

@Injectable({
  providedIn: 'root'
})
export class ApplicationContextService {
  private _applicationContext: ApplicationContext;
  private _contextChangedSubject: Subject<ApplicationContext> = new Subject<ApplicationContext>();

  constructor(
    private readonly _authService: AuthenticationService,
    private readonly _studiesCompanyAutoSelect: StudiesCompanyAutoSelectService,
  ) { }

  get context(): Observable<ApplicationContext> {
    if (this._applicationContext) {
      return of(this._applicationContext);
    }
    return this.initializeApplicationContext().pipe(map(context => {
      this._applicationContext = context;
      this._contextChangedSubject.next(context);
      return context;
    }));
  }

  get contextChanged$(): Subject<ApplicationContext> {
    return this._contextChangedSubject;
  }

  resetContext() {
    this._applicationContext = null;
  }

  private initializeApplicationContext = (): Observable<ApplicationContext> => {
    return this._authService.resolveLoggedInUser().pipe(
      switchMap((user: User | null) => {
        if (!user?.id) {
          return of({
            currentlyLoggedInUser: null,
            userCompanies: [],
            companyLicenses: [],
          } as ApplicationContext);
        }
        return this._authService.getUserCompanies(user.id).pipe(
          catchError(() => of([] as UserCompany[])),
          switchMap((userCompanies: UserCompany[]) => this._authService.getCompanyProductsLicenses().pipe(
            catchError(() => of([] as string[])),
            map((companyLicenses: string[]) => ({
              currentlyLoggedInUser: user,
              userCompanies,
              companyLicenses
            }))
          ))
        );
      }),
      switchMap((context: ApplicationContext) => this.maybeAutoSelectOrganization(context))
    );
  }

  private maybeAutoSelectOrganization(
    context: ApplicationContext
  ): Observable<ApplicationContext> {
    const needsOrg =
      resolveStudiesOrganizationContext({
        companyId: context.currentlyLoggedInUser?.companyId,
        userCompanies: context.userCompanies,
      }) === 'select-company';

    if (!needsOrg || getVisibleUserCompanies(context.userCompanies).length !== 1) {
      return of(context);
    }

    return this._studiesCompanyAutoSelect
      .tryAutoSelectSingleCompany(context, { navigate: false })
      .pipe(
        switchMap((selected) =>
          selected ? this.initializeApplicationContext() : of(context)
        )
      );
  }
}
